import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { validateShortcodeCheckdigits } from '@/utils/shortcode';
import { APIRoutes } from '@/services/api.routes';

/**
 * ShortcodeResolver component
 * Handles /s/{code}/{checkdigits} URLs
 * - Validates checkdigits
 * - Resolves the shortcode via backend
 * - Redirects to the resolved URL
 */
export default function ShortcodeResolver() {
  const { code, checkdigits } = useParams<{ code: string; checkdigits: string }>();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const resolveLink = async () => {
      if (code === undefined || checkdigits === undefined) {
        setError('Ungültiger Link: Code oder Prüfziffer fehlt');
        return;
      }

      // Validate checkdigits
      const isValid = await validateShortcodeCheckdigits(code, checkdigits);
      if (!isValid) {
        setError('Ungültiger Link');
        return;
      }

      try {
        // Resolve shortcode via backend
        const response = await APIRoutes.resolveShortcode(code);

        // Redirect to the resolved URL
        const redirectUrl = response.redirectUrl;

        // Check if it's an external URL or relative path
        if (redirectUrl.startsWith('http://') || redirectUrl.startsWith('https://')) {
          // External URL - use window.location
          window.location.href = redirectUrl;
        } else {
          // Relative path - use React Router navigate
          navigate(redirectUrl);
        }
      } catch (err) {
        console.error('Failed to resolve shortcode:', err);
        setError('Link konnte nicht aufgelöst werden');
      }
    };

    resolveLink();
  }, [code, checkdigits, navigate]);

  if (error !== null) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <div className="text-6xl mb-4">⚠️</div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            {error}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Der angegebene Link ist ungültig oder existiert nicht mehr.
          </p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer transition-colors"
          >
            Zur Startseite
          </button>
        </div>
      </div>
    );
  }

  // Loading state - full screen spinner
  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="text-center">
        <div className="inline-block animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mb-4"></div>
        <p className="text-gray-600 dark:text-gray-400 text-lg">
          Link wird aufgelöst...
        </p>
      </div>
    </div>
  );
}
